import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type WCtaVariant = 'primary' | 'outline' | 'ghost';

type Props = {
  children: ReactNode;
  beforeClick?: () => boolean;
  onClick?: () => void | Promise<void>;
  onDone?: () => void;
  disabled?: boolean;
  dimmed?: boolean;
  variant?: WCtaVariant;
  working?: string;
  minWorkingMs?: number;
  doneHoldMs?: number;
};

const KIND: Record<WCtaVariant, string> = {
  primary: 'dsb-primary',
  outline: 'dsb-secondary',
  ghost: 'dsb-tertiary',
};

const Spinner = () => (
  <svg className="dsb-load-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeDasharray="44"
      strokeDashoffset="14"
    />
  </svg>
);

const Check = () => (
  <svg className="dsb-load-check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      pathLength="1"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const WCta = ({
  children,
  beforeClick,
  onClick,
  onDone,
  disabled,
  dimmed,
  variant = 'primary',
  working,
  minWorkingMs = 0,
  doneHoldMs = 480,
}: Props) => {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'working' | 'done'>('idle');
  const busy = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (busy.current || state !== 'idle') return;
    if (beforeClick?.() === false) return;
    busy.current = true;
    setState('working');
    try {
      const started = Date.now();
      await onClick?.();
      const remaining = minWorkingMs - (Date.now() - started);
      if (remaining > 0) await wait(remaining);
      if (!alive.current) return;
      setState('done');
      await wait(doneHoldMs);
      if (!alive.current) return;
      onDone?.();
    } catch {
      busy.current = false;
      if (alive.current) setState('idle');
    }
  }, [state, beforeClick, onClick, onDone, minWorkingMs, doneHoldMs]);

  const className = ['dsb', KIND[variant], 'dsb-l', 'dsb-block', 'dsb-loading']
    .concat(dimmed && state === 'idle' ? 'is-dimmed' : [])
    .concat(state === 'working' ? 'is-working' : state === 'done' ? 'is-done' : [])
    .join(' ');

  const label =
    state === 'working'
      ? working || t('wreg.btn.working')
      : state === 'done'
        ? t('wreg.btn.done')
        : children;

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      aria-busy={state === 'working'}
      onClick={handleClick}
    >
      <span className="dsb-load">
        {state === 'working' ? <Spinner /> : state === 'done' ? <Check /> : null}
      </span>
      <span className="dsb-lbl">{label}</span>
    </button>
  );
};

export { WCta };
